import React, { useState } from 'react';
import {
  Calendar,
  ChevronRight,
  ChevronDown,
  Info,
  TrendingUp,
  TrendingDown,
  Receipt,
  Layers,
  BarChart2,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { MonthlyFinancialActivityRecord, AnnualFinancialSummaryRecord } from '@/services/reportService';
import { formatCurrency, formatNumber } from '@/lib/utils';

interface MonthlyFinancialActivityViewProps {
  records: MonthlyFinancialActivityRecord[];
  selectedYear?: string;
  annualSummary?: AnnualFinancialSummaryRecord[];
}

export const MonthlyFinancialActivityView: React.FC<MonthlyFinancialActivityViewProps> = ({
  records,
  selectedYear = 'all',
  annualSummary = [],
}) => {
  const [expandedMonthKey, setExpandedMonthKey] = useState<string | null>(null);

  const isAllYears = selectedYear === 'all';
  const yearTitle = isAllYears ? 'Multi-Year Trend & Comparison' : selectedYear;

  // Calculate totals for active table
  const totalSales = records.reduce((sum, r) => sum + r.pigSales, 0);
  const totalCosts = records.reduce((sum, r) => sum + r.totalBookedCosts, 0);
  const totalMovement = totalSales - totalCosts;

  return (
    <div className="space-y-4">
      {/* View Section Heading */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 pb-1 border-b border-slate-100">
        <div>
          <h2 className="text-base sm:text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <span>Monthly Financial Activity — {yearTitle}</span>
          </h2>
          <p className="text-xs text-slate-500">
            {isAllYears
              ? 'Annual comparative summary and chronological calendar ledger transactions across all years.'
              : `Booked calendar transactions and monthly cashflow movements for ${selectedYear} (${records.length} active months).`}
          </p>
        </div>
      </div>

      {/* 1. Header Notice */}
      <div className="p-3.5 bg-blue-50/70 border border-blue-200 rounded-xl flex items-start gap-2.5 text-xs text-blue-900 shadow-sm">
        <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <p>
          <span className="font-semibold text-blue-950">Accounting Principle:</span> Monthly Financial Activity reflects cash and ledger movements recorded in each calendar month. Because feeding and sales for a single cycle span multiple months, monthly net movement reflects calendar cashflow rather than a closed-cycle P&L.
        </p>
      </div>

      {/* 2. Multi-Year Annual Comparative Summary (Shown when All Years is selected) */}
      {isAllYears && annualSummary.length > 0 && (
        <Card className="border-slate-200 shadow-sm bg-white overflow-hidden">
          <CardHeader className="pb-2 pt-3.5 px-4 bg-slate-50/80 border-b border-slate-200">
            <CardTitle className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
              <BarChart2 className="w-4 h-4 text-emerald-700" />
              Annual Multi-Year Comparative Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-slate-100/60 text-slate-600 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="py-2.5 px-4 font-bold text-slate-900">Year</th>
                    <th className="py-2.5 px-3 text-center">Active Months</th>
                    <th className="py-2.5 px-3 text-right">Pig Sales (1010)</th>
                    <th className="py-2.5 px-3 text-right">Booked Costs (3010–3051)</th>
                    <th className="py-2.5 px-3 text-right font-bold">Net Movement</th>
                    <th className="py-2.5 px-4 text-right">Net Margin %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {annualSummary.map((yr) => (
                    <tr key={yr.year} className="hover:bg-slate-50 font-mono transition-colors">
                      <td className="py-2.5 px-4 font-bold text-slate-900 text-sm">
                        {yr.year}
                      </td>
                      <td className="py-2.5 px-3 text-center text-slate-600">
                        {yr.activeMonthsCount} months
                      </td>
                      <td className="py-2.5 px-3 text-right font-semibold text-emerald-800">
                        {formatCurrency(yr.pigSales)}
                      </td>
                      <td className="py-2.5 px-3 text-right font-semibold text-rose-800">
                        {formatCurrency(yr.totalBookedCosts)}
                      </td>
                      <td className={`py-2.5 px-3 text-right font-bold ${yr.netBookedMovement >= 0 ? 'text-indigo-900' : 'text-rose-700'}`}>
                        {formatCurrency(yr.netBookedMovement)}
                      </td>
                      <td className="py-2.5 px-4 text-right font-bold text-slate-800">
                        {yr.grossMarginPct !== null ? `${yr.grossMarginPct}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards for Multi-Year Annual Comparative Overview (< 768px) */}
            <div className="md:hidden divide-y divide-slate-200 p-3 space-y-3">
              {annualSummary.map((yr) => (
                <div key={yr.year} className="bg-slate-50/70 rounded-xl p-3 border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-900 text-sm font-mono">{yr.year}</span>
                    <span className="text-xs text-slate-500 font-medium">{yr.activeMonthsCount} months active</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs font-mono pt-1.5 border-t border-slate-200">
                    <div>
                      <span className="text-[10px] text-slate-400 block font-sans uppercase">Pig Sales (1010)</span>
                      <span className="font-bold text-emerald-800">{formatCurrency(yr.pigSales)}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 block font-sans uppercase">Booked Costs</span>
                      <span className="font-bold text-rose-800">{formatCurrency(yr.totalBookedCosts)}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 block font-sans uppercase">Net Movement</span>
                      <span className={`font-bold ${yr.netBookedMovement >= 0 ? 'text-indigo-900' : 'text-rose-700'}`}>
                        {formatCurrency(yr.netBookedMovement)}
                      </span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 block font-sans uppercase">Net Margin %</span>
                      <span className="font-bold text-slate-800">
                        {yr.grossMarginPct !== null ? `${yr.grossMarginPct}%` : '—'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 2. Desktop 7-Column Table (Fits 1280px Screen Without Horizontal Scroll) */}
      <div className="hidden md:block bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-left text-xs border-collapse">
          <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
            <tr>
              <th className="py-2.5 px-3 w-32">Month</th>
              <th className="py-2.5 px-3 text-right">Pig Sales (1010)</th>
              <th className="py-2.5 px-3 text-right">Piglets (3010)</th>
              <th className="py-2.5 px-3 text-right">Feed Allocated (3020)</th>
              <th className="py-2.5 px-3 text-right">Meds & Direct (3030+)</th>
              <th className="py-2.5 px-3 text-right font-bold text-slate-900">Total Costs (₱)</th>
              <th className="py-2.5 px-3 text-right font-bold text-slate-900">Net Movement (₱)</th>
              <th className="py-2.5 px-2 text-center w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {records.map((r) => {
              const isExpanded = expandedMonthKey === r.monthKey;
              return (
                <React.Fragment key={r.monthKey}>
                  <tr
                    onClick={() => setExpandedMonthKey(isExpanded ? null : r.monthKey)}
                    className={`hover:bg-slate-50 cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50/80 font-medium' : ''}`}
                  >
                    <td className="py-2.5 px-3 font-bold font-mono text-slate-900">
                      {r.monthLabel}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono font-semibold text-slate-900">
                      {r.pigSales > 0 ? (
                        formatCurrency(r.pigSales)
                      ) : (
                        <span className="text-slate-400 font-normal">₱0.00</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-700">
                      {r.piglets > 0 ? formatCurrency(r.piglets) : <span className="text-slate-400">₱0.00</span>}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-700">
                      {r.feeds > 0 ? (
                        formatCurrency(r.feeds)
                      ) : (
                        <span className="text-slate-400">₱0.00</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-700">
                      {r.medsAndDirectExpenses > 0 ? (
                        formatCurrency(r.medsAndDirectExpenses)
                      ) : (
                        <span className="text-slate-400">₱0.00</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono font-bold text-rose-800">
                      {formatCurrency(r.totalBookedCosts)}
                    </td>
                    <td className={`py-2.5 px-3 text-right font-mono font-bold ${r.netBookedMovement >= 0 ? 'text-emerald-800' : 'text-rose-700'}`}>
                      {formatCurrency(r.netBookedMovement)}
                    </td>
                    <td className="py-2.5 px-2 text-center text-slate-400">
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </td>
                  </tr>

                  {/* Progressive Disclosure Account Drilldown */}
                  {isExpanded && (
                    <tr className="bg-slate-50/90 border-b border-slate-200">
                      <td colSpan={8} className="p-4">
                        <div className="bg-white p-3.5 rounded-lg border border-slate-200 space-y-2 text-xs shadow-inner">
                          <div className="flex items-center justify-between font-bold text-slate-800 border-b border-slate-100 pb-2">
                            <span>Detailed Chart of Accounts Breakdown — {r.monthLabel}</span>
                            <div className="flex gap-3 text-[11px] font-mono">
                              {r.momRevenueGrowthPct !== null && (
                                <span className={r.momRevenueGrowthPct >= 0 ? 'text-emerald-700' : 'text-rose-600'}>
                                  MoM Sales: {r.momRevenueGrowthPct > 0 ? `+${r.momRevenueGrowthPct}%` : `${r.momRevenueGrowthPct}%`}
                                </span>
                              )}
                              {r.momCostGrowthPct !== null && (
                                <span className="text-slate-600">
                                  MoM Costs: {r.momCostGrowthPct > 0 ? `+${r.momCostGrowthPct}%` : `${r.momCostGrowthPct}%`}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 pt-1 font-mono text-[11px]">
                            {Object.values(r.accountBreakdown).map((acc) => (
                              <div key={acc.code} className="p-2 bg-slate-50 rounded border border-slate-100 flex flex-col justify-between">
                                <div className="text-[10px] text-slate-400 font-sans font-semibold">
                                  Account [{acc.code}]
                                </div>
                                <div className="font-bold text-slate-800 mt-0.5">{formatCurrency(acc.sum)}</div>
                                <div className="text-[9px] text-slate-400 font-sans">{acc.count} transactions</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-100 text-slate-900 font-bold border-t-2 border-slate-300">
            <tr>
              <td className="py-2.5 px-3 font-mono">TOTAL ACTIVITY</td>
              <td className="py-2.5 px-3 text-right font-mono">{formatCurrency(totalSales)}</td>
              <td className="py-2.5 px-3 text-right font-mono" colSpan={3}></td>
              <td className="py-2.5 px-3 text-right font-mono text-rose-900">{formatCurrency(totalCosts)}</td>
              <td className={`py-2.5 px-3 text-right font-mono ${totalMovement >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>
                {formatCurrency(totalMovement)}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* 3. Mobile Responsive Cards (Screens < 768px) */}
      <div className="md:hidden space-y-3">
        {records.map((r) => {
          const isExpanded = expandedMonthKey === r.monthKey;
          return (
            <div key={r.monthKey} className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-bold font-mono text-slate-900 text-sm">{r.monthLabel}</span>
                <span className={`font-mono text-xs font-bold ${r.netBookedMovement >= 0 ? 'text-emerald-800' : 'text-rose-700'}`}>
                  Net: {formatCurrency(r.netBookedMovement)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 text-xs">
                <div>
                  <span className="text-[10px] text-slate-400 block uppercase">Pig Sales (1010)</span>
                  <span className="font-mono font-bold text-slate-900">{formatCurrency(r.pigSales)}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 block uppercase">Total Costs</span>
                  <span className="font-mono font-bold text-rose-800">{formatCurrency(r.totalBookedCosts)}</span>
                </div>
              </div>

              <button
                onClick={() => setExpandedMonthKey(isExpanded ? null : r.monthKey)}
                className="w-full text-center py-2.5 min-h-[44px] text-xs text-indigo-700 font-semibold border-t border-slate-100 flex items-center justify-center gap-1.5 active:bg-slate-50 transition-colors"
              >
                <span>{isExpanded ? 'Hide Accounts' : 'View Account Breakdown'}</span>
                {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>

              {isExpanded && (
                <div className="bg-slate-50 p-2.5 rounded-lg space-y-1.5 text-[11px] font-mono border border-slate-200">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Piglets (3010):</span>
                    <span className="font-semibold text-slate-800">{formatCurrency(r.piglets)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Feed Allocated (3020):</span>
                    <span className="font-semibold text-slate-800">{formatCurrency(r.feeds)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Meds & Direct (3030+):</span>
                    <span className="font-semibold text-slate-800">{formatCurrency(r.medsAndDirectExpenses)}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Mobile Total Activity Summary Card */}
        <div className="bg-slate-100 rounded-xl p-3.5 border-2 border-slate-300 space-y-2">
          <div className="font-bold text-slate-900 text-xs font-mono uppercase tracking-wider">
            Total Activity Summary
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs font-mono pt-1 border-t border-slate-300">
            <div>
              <span className="text-[10px] text-slate-500 block font-sans uppercase">Total Sales (1010)</span>
              <span className="font-bold text-slate-900">{formatCurrency(totalSales)}</span>
            </div>
            <div>
              <span className="text-[10px] text-slate-500 block font-sans uppercase">Total Costs</span>
              <span className="font-bold text-rose-900">{formatCurrency(totalCosts)}</span>
            </div>
            <div className="col-span-2 pt-1 border-t border-slate-200 flex justify-between items-center">
              <span className="text-[10px] text-slate-600 font-sans uppercase font-bold">Net Calendar Movement</span>
              <span className={`font-bold text-sm ${totalMovement >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>
                {formatCurrency(totalMovement)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
